import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import { Show } from "@/components/core";
import { useTemplateSelectorController } from "./useTemplateSelectorController";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { AlertCircle, PencilIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { useAtomValue } from "jotai";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export interface TemplateSelectorProps {
  selectedTemplateId?: number;
  onSelectTemplate: (templateId: number) => void;
}

export const TemplateSelector = (props: TemplateSelectorProps) => {
  const controller = useTemplateSelectorController(props);
  const navigate = useNavigate();
  const selectedFund = useAtomValue(SelectedFundAtom);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Entity Type</TableHead>
            <TableHead>Created At</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Tags</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className="[&_tr:nth-child(even)]:bg-muted/50">
          {controller.templates.map((template) => {
            return (
              <TableRow
                key={template.importTemplateId}
                className={cn(
                  "cursor-pointer hover:bg-muted/70 transition-colors",
                  controller.selectedTemplate?.importTemplateId ===
                    template.importTemplateId &&
                    "bg-primary/10 hover:bg-primary/15 border-l-4 border-l-primary"
                )}
                onClick={(e) => {
                  controller.selectTemplate(template.importTemplateId!);
                }}
              >
                <TableCell className="font-medium">{template.name}</TableCell>
                <TableCell>{template.entityType}</TableCell>
                <TableCell>{formatDate(template.createdAt)}</TableCell>
                <TableCell className="max-w-[200px] truncate">
                  {template.description}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    <Show
                      if={template.tags !== "" && template.tags !== undefined}
                    >
                      {template.tags.split(", ").map((tag, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="text-xs"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </Show>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <div className="mt-4">
        <Show
          if={
            selectedFund === undefined &&
            controller.selectedTemplate?.entityType === "Transaction"
          }
        >
          <Alert variant="destructive" className="w-full mx-auto ">
            <AlertTitle>No fund selected</AlertTitle>
            <AlertDescription>
              Please select a fund to import transactions.
            </AlertDescription>
          </Alert>
        </Show>

        <Show
          if={
            selectedFund === undefined &&
            controller.selectedTemplate?.entityType === "Holding"
          }
        >
          <Alert variant="destructive" className="mt-4 w-full  mx-auto">
            <AlertTitle>No fund selected</AlertTitle>
            <AlertDescription>
              Please select a fund to import holdings.
            </AlertDescription>
          </Alert>
        </Show>
      </div>

      {controller.selectedTemplate && (
        <div className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Template Fields</CardTitle>
                <CardDescription>
                  Field mapping for {controller.selectedTemplate?.name}
                </CardDescription>
              </div>
              {/* <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigate(
                    `/app/imports/new?templateId=${controller.selectedTemplate?.importTemplateId}`
                  );
                }}
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Template
              </Button> */}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>From Field</TableHead>
                    <TableHead>To Field</TableHead>
                    <TableHead>Data Type</TableHead>
                    <TableHead>Required</TableHead>
                    <TableHead>Precision</TableHead>
                    <TableHead>Date Format</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="[&_tr:nth-child(even)]:bg-muted/50">
                  {controller.selectedTemplate?.fields.map((field, index) => (
                    <TableRow key={`${index}-${field.toField}`}>
                      <TableCell className="font-medium">
                        {field.fromField}
                      </TableCell>
                      <TableCell>{field.toField}</TableCell>
                      <TableCell>{field.dataType}</TableCell>
                      <TableCell>{field.required ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        {field.precision !== null ? field.precision : "-"}
                      </TableCell>
                      <TableCell>{field.dateFormat || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};
